import { routeEstimateResponseSchema } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { type GeoLocation, useDeviceGeoLocationIfAllowed } from "@src/appV2/Location";
import {
  type QueryClient,
  type QueryKey,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import { minutesToMilliseconds } from "date-fns";
import { isFinite, round } from "lodash";
import { useEffect } from "react";

import { computeArrivalData, type ComputedArrivalData } from "../computeArrivalData";
import { useArrivalTimeConfig } from "../useArrivalTimeConfig";

export type RouteEstimateProvider = "AWS_LOCATION" | "GOOGLE_MAPS";
interface UseShiftRouteEstimateParams {
  source?: GeoLocation;
  destination?: GeoLocation;
  provider?: RouteEstimateProvider;
  workerId: string;
  shiftId?: string;
  shiftInviteId?: string;
  workplaceId: string;
  departureTimeIso?: string;
  shiftStartIso?: string;
  shiftAuctionEndDate?: Date;
}

export const GET_ROUTE_ESTIMATES_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}/route-estimates`;

function roundCoordinates(coordinates: GeoLocation): GeoLocation {
  return {
    latitude: round(coordinates.latitude, 4),
    longitude: round(coordinates.longitude, 4),
  };
}

/**
 * Query key for shift route estimates.
 * Note: We intentionally don't include source coordinates in the key.
 * Combined with staleTime of 5 minutes, this prevents:
 * 1. Flickering when device location temporarily becomes unavailable
 * 2. Unnecessary refetches when worker's location changes slightly
 * The route estimate is meant for pre-booking decisions, not real-time navigation.
 */
function getQueryKeyForShiftRouteEstimate(shiftOrShiftInviteId: string | undefined): QueryKey {
  return ["shiftRouteEstimate", shiftOrShiftInviteId];
}

export function getCachedShiftRouteEstimate(params: {
  queryClient: QueryClient;
  shiftOrShiftInviteId: string | undefined;
}): ComputedArrivalData | undefined {
  const { queryClient, shiftOrShiftInviteId } = params;

  return queryClient.getQueryData<ComputedArrivalData>(
    getQueryKeyForShiftRouteEstimate(shiftOrShiftInviteId)
  );
}

/**
 * Gets cached shift route estimate
 */
export function useGetCachedShiftRouteEstimate(
  shiftOrShiftInviteId: string | undefined
): ComputedArrivalData | undefined {
  const queryClient = useQueryClient();

  return isDefined(shiftOrShiftInviteId)
    ? getCachedShiftRouteEstimate({
        queryClient,
        shiftOrShiftInviteId,
      })
    : undefined;
}

/**
 * This hook fetches worker location and gets the route estimate and computing arrival data.
 * It handles logging for missing data as well.
 */

export function useShiftRouteEstimate(
  params: UseShiftRouteEstimateParams & {
    onSuccess?: (data: ComputedArrivalData) => void;
    onError?: (error: Error) => void;
  },
  options: UseQueryOptions<ComputedArrivalData> = {}
): UseQueryResult<ComputedArrivalData> {
  const {
    source: sourceParameter,
    destination,
    provider = "GOOGLE_MAPS",
    workerId,
    shiftId,
    workplaceId,
    shiftInviteId,
    departureTimeIso,
    shiftStartIso,
    shiftAuctionEndDate,
    onSuccess,
    onError,
  } = params;

  // We assume that by this point, the user has granted permission to access their location
  const { data: deviceGeoLocation } = useDeviceGeoLocationIfAllowed({
    enabled: !isDefined(sourceParameter),
  });
  const source = sourceParameter ?? deviceGeoLocation?.geoLocation;

  const shiftOrShiftInviteId = shiftId ?? shiftInviteId;

  const isSourceDefined =
    isDefined(source) && isFinite(source.longitude) && isFinite(source.latitude);

  const isDestinationDefined =
    isDefined(destination) && isFinite(destination.longitude) && isFinite(destination.latitude);

  const roundedSourceCoordinates = isDefined(source) ? roundCoordinates(source) : undefined;
  const { longitude: workerLongitude, latitude: workerLatitude } = roundedSourceCoordinates ?? {};

  const { gracePeriodInMinutesForDriveETA } = useArrivalTimeConfig();

  useEffect(() => {
    // some of the data might be missing because it hasn't been fetched in the upper component yet.
    // It should pass 'enabled' as false in these cases, so we're filtering it out here.
    if (!options.enabled) {
      return;
    }

    if (!isSourceDefined) {
      logEvent(APP_V2_APP_EVENTS.ARRIVAL_TIME_UNAVAILABLE, {
        reason: "Worker location is missing",
        workerId,
        shiftId,
        shiftInviteId,
        workplaceId,
      });
      return;
    }

    if (!isDestinationDefined) {
      logEvent(APP_V2_APP_EVENTS.ARRIVAL_TIME_UNAVAILABLE, {
        reason: "Destination is missing",
        workerId,
        shiftId,
        shiftInviteId,
        workplaceId,
        workerLatitude,
        workerLongitude,
      });
    }
  }, [
    isSourceDefined,
    isDestinationDefined,
    options.enabled,
    workerId,
    shiftId,
    shiftInviteId,
    workplaceId,
    workerLatitude,
    workerLongitude,
  ]);

  const isHookEnabled =
    isSourceDefined &&
    isDestinationDefined &&
    isDefined(shiftOrShiftInviteId) &&
    isDefined(shiftStartIso) &&
    isDefined(gracePeriodInMinutesForDriveETA) &&
    options.enabled !== false;

  return useQuery<ComputedArrivalData, Error, ComputedArrivalData, any>({
    queryKey: getQueryKeyForShiftRouteEstimate(shiftOrShiftInviteId),
    queryFn: async () => {
      const response = await get({
        url: GET_ROUTE_ESTIMATES_URL,
        queryParams: {
          source: {
            coordinates: [workerLongitude, workerLatitude],
          },
          destination: {
            coordinates: [destination?.longitude, destination?.latitude],
          },
          provider,
          departureTime: departureTimeIso,
        },
        responseSchema: routeEstimateResponseSchema,
      });

      // We deliberately convert the route estimate to computed arrival data here instead
      // of a select clause so we can easily access it via the query cache
      return computeArrivalData({
        routeEstimate: response.data,
        shiftStartIso: shiftStartIso ?? "",
        gracePeriodInMinutes: gracePeriodInMinutesForDriveETA,
        shiftAuctionEndDate,
      });
    },
    onSuccess: (data: ComputedArrivalData) => {
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
      logEvent(APP_V2_APP_EVENTS.ARRIVAL_TIME_UNAVAILABLE, {
        reason: "No ETA available from the server",
        workerId,
        shiftId,
        shiftInviteId,
        workplaceId,
        workerLatitude,
        workerLongitude,
      });
    },
    staleTime: minutesToMilliseconds(5),
    ...options,
    enabled: isHookEnabled,
  });
}
